import React from "react";
import { api } from "../lib/api";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  isBefore,
  startOfDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import BookingDialog, { availableTypesFor } from "./BookingDialog";

const TYPE_STYLES = {
  day_use: {
    label: "Day Use",
    short: "Day",
    chip: "bg-amber-400/15 text-amber-200 border-amber-400/30",
    dot: "bg-amber-400",
  },
  night_use: {
    label: "Night Use",
    short: "Night",
    chip: "bg-indigo-400/15 text-indigo-200 border-indigo-400/30",
    dot: "bg-indigo-400",
  },
  whole_day: {
    label: "Overnight Use",
    short: "Over",
    chip: "bg-rose-400/15 text-rose-200 border-rose-400/30",
    dot: "bg-rose-400",
  },
};

export default function MonthlySchedule({ forceOwnerMode = false } = {}) {
  const [month, setMonth] = React.useState(new Date());
  const [bookings, setBookings] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [rates, setRates] = React.useState([]);
  const [settings, setSettings] = React.useState(null);
  const [promos, setPromos] = React.useState([]);
  const [addonServices, setAddonServices] = React.useState([]);
  const [pickedDate, setPickedDate] = React.useState(null);

  // Public visitors never see owner mode. Owner mode is only set by AdminBlockDates page (forceOwnerMode prop).

  const load = React.useCallback(async (anchor) => {
    setLoading(true);
    const start = format(startOfWeek(startOfMonth(anchor), { weekStartsOn: 0 }), "yyyy-MM-dd");
    const end = format(endOfWeek(endOfMonth(anchor), { weekStartsOn: 0 }), "yyyy-MM-dd");
    try {
      const r = await api.get(`/availability?start=${start}&end=${end}`);
      setBookings(r.data?.bookings || {});
      setUpdatedAt(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(month); }, [month, load]);

  React.useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data || []));
    api.get("/settings").then((r) => setSettings(r.data || {}));
    api.get("/promos").then((r) => setPromos(r.data || [])).catch(() => {});
    api.get("/services").then((r) =>
      setAddonServices(
        (r.data || []).filter((s) => s.active !== false && s.is_addon && Number(s.price) > 0)
      )
    ).catch(() => {});
  }, []);

  // Real-time poll
  React.useEffect(() => {
    const id = setInterval(() => load(month), 15000);
    return () => clearInterval(id);
  }, [month, load]);

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 0 }),
  });

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Stat tiles count UNIQUE CONFIRMED bookings only.
  // - Multi-day bookings appear in `bookings` once per spanned date; we dedupe by id.
  // - Pending bookings are excluded from the count (they show in calendar with a dashed
  //   border but don't yet inflate the "Reservations" stat).
  const totals = React.useMemo(() => {
    const seen = new Set();
    const acc = { total: 0, day_use: 0, night_use: 0, whole_day: 0 };
    for (const items of Object.values(bookings)) {
      for (const b of items) {
        if (b.status !== "confirmed") continue;
        if (!b.id || seen.has(b.id)) continue;
        seen.add(b.id);
        acc.total += 1;
        acc[b.booking_type] = (acc[b.booking_type] || 0) + 1;
      }
    }
    return acc;
  }, [bookings]);

  const today = startOfDay(new Date());

  const handleDayClick = (d) => {
    if (!isSameMonth(d, month)) return;
    if (isBefore(d, today)) return;
    setPickedDate(d);
  };

  const pickedIso = pickedDate ? format(pickedDate, "yyyy-MM-dd") : null;
  const pickedBookings = pickedIso ? bookings[pickedIso] || [] : [];

  return (
    <section
      id="schedule"
      data-testid="monthly-schedule-section"
      className="py-16 md:py-32 px-5 sm:px-6 md:px-12 lg:px-24 max-w-7xl mx-auto"
    >
      <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-5 md:gap-6">
        <div className="w-full lg:w-auto">
          <p className="overline mb-3 md:mb-4">Real-Time Schedule</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            This Month at a <span className="italic text-[#C5A059]">Glance</span>
          </h2>
          <p className="text-[#A1A4B5] text-xs sm:text-sm mt-3 max-w-xl">
            Click any open date to book — Overnight Use any day (2 PM – 10 AM), or Day Use (9 AM – 5 PM) and Night Use (7 PM – 8 AM) on Mondays–Thursdays.
          </p>
          <div className="gold-divider mt-5 md:mt-6" />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 w-full lg:w-auto justify-between lg:justify-end">
          <button
            onClick={() => setMonth((m) => subMonths(m, 1))}
            data-testid="schedule-prev-month"
            className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors flex-shrink-0"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="font-serif-display text-xl sm:text-2xl px-2 sm:px-4 flex-1 lg:flex-none lg:min-w-[200px] text-center" data-testid="schedule-current-month">
            {format(month, "MMMM yyyy")}
          </div>
          <button
            onClick={() => setMonth((m) => addMonths(m, 1))}
            data-testid="schedule-next-month"
            className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors flex-shrink-0"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => load(month)}
            data-testid="schedule-refresh"
            className="ml-1 sm:ml-2 w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors flex-shrink-0"
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        <StatCard label="Confirmed Reservations" value={totals.total} accent="#C5A059" />
        <StatCard label="Overnight Use" value={totals.whole_day} dot="bg-rose-400" />
        <StatCard
          label="Day / Night Use"
          value={totals.day_use + totals.night_use}
          dot="bg-amber-400"
        />
      </div>

      {/* Calendar grid */}
      <div className="glass rounded-2xl p-2.5 sm:p-4 md:p-6">
        <div className="grid grid-cols-7 mb-2">
          {weekdays.map((w) => (
            <div
              key={w}
              className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-[#A1A4B5] text-center py-1.5 sm:py-2"
            >
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 md:gap-2">
          {days.map((d) => {
            const iso = format(d, "yyyy-MM-dd");
            const inMonth = isSameMonth(d, month);
            const isPast = isBefore(d, today);
            const todayCell = isToday(d);
            const items = bookings[iso] || [];
            const avail = availableTypesFor(items);
            const isFullyBooked = inMonth && items.length > 0 && avail.length === 0;
            const interactive = inMonth && !isPast;
            const promoOnDay =
              inMonth && !isPast &&
              promos.some((p) => p.active && iso >= p.start_date && iso <= p.end_date);

            return (
              <button
                key={iso}
                onClick={() => handleDayClick(d)}
                disabled={!interactive}
                data-testid={`schedule-day-${iso}`}
                className={`text-left relative aspect-square md:aspect-[5/4] rounded-md sm:rounded-lg p-1 sm:p-2 md:p-2.5 border transition-all overflow-hidden flex flex-col group ${
                  inMonth
                    ? "border-[var(--border)] bg-[#13151D]"
                    : "border-transparent bg-[#0F1118] opacity-40"
                } ${todayCell ? "ring-1 ring-[#C5A059]" : ""} ${
                  interactive
                    ? "hover:border-[#C5A059] hover:bg-[#1a1d28] cursor-pointer"
                    : "cursor-default"
                } ${isPast && inMonth ? "opacity-50" : ""}`}
              >
                <div
                  className={`text-[11px] sm:text-xs md:text-sm font-medium leading-none ${
                    todayCell
                      ? "text-[#C5A059]"
                      : inMonth
                      ? "text-white"
                      : "text-[#A1A4B5]"
                  }`}
                >
                  {format(d, "d")}
                </div>
                {promoOnDay && (
                  <span
                    className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 text-[7px] sm:text-[8px] uppercase tracking-[0.06em] bg-[#C5A059] text-black font-semibold rounded px-1 py-0.5 leading-none"
                    data-testid={`promo-day-${iso}`}
                  >
                    <span className="hidden sm:inline">Promo</span>
                    <span className="sm:hidden">%</span>
                  </span>
                )}

                <div className="mt-auto space-y-0.5 sm:space-y-1">
                  {items.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 sm:gap-1">
                      {items.map((b, i) => {
                        const s = TYPE_STYLES[b.booking_type];
                        return (
                          <span
                            key={i}
                            className={`text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-normal sm:tracking-[0.05em] md:tracking-[0.08em] px-1 sm:px-1.5 py-0.5 rounded border max-w-full truncate inline-block leading-none ${s.chip} ${
                              b.status === "pending" ? "border-dashed" : ""
                            }`}
                          >
                            <span className="hidden md:inline">{s.label}</span>
                            <span className="md:hidden">{s.short}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {inMonth && !isPast && !isFullyBooked && (
                    <div
                      className={`text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.15em] ${
                        items.length > 0 ? "text-[#C5A059]" : "text-emerald-300/80"
                      } group-hover:text-[#DFB971] transition-colors`}
                    >
                      {items.length > 0 ? "+ Add" : "Open"}
                    </div>
                  )}
                  {inMonth && !isPast && isFullyBooked && (
                    <div className="text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.15em] text-rose-300/80">
                      Full
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-[var(--border)] flex-wrap gap-2 sm:gap-3 text-[10px] sm:text-[11px] text-[#A1A4B5]">
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            {Object.entries(TYPE_STYLES).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1 sm:gap-1.5">
                <span className={`w-2 h-2 rounded-full ${v.dot}`} /> {v.label}
              </span>
            ))}
            <span className="flex items-center gap-1 sm:gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> <span className="hidden sm:inline">Open · click to book</span><span className="sm:hidden">Open</span>
            </span>
            <span className="flex items-center gap-1 sm:gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-[#C5A059]" /> Promo
            </span>
            <span className="flex items-center gap-1 sm:gap-1.5">
              <span className="w-2 h-2 rounded border border-dashed border-white/40" /> Pending
            </span>
          </div>
          {updatedAt && (
            <span data-testid="schedule-updated-at" className="w-full sm:w-auto">
              <span className="hidden sm:inline">Updated </span>{format(updatedAt, "h:mm:ss a")}<span className="hidden sm:inline"> · refreshes every 15s</span>
            </span>
          )}
        </div>
      </div>

      <BookingDialog
        open={!!pickedDate}
        onOpenChange={(v) => !v && setPickedDate(null)}
        date={pickedDate}
        bookingsForDate={pickedBookings}
        bookingsByDate={bookings}
        rates={rates}
        promos={promos}
        addonServices={addonServices}
        settings={settings || {}}
        ownerMode={forceOwnerMode}
        onBooked={() => load(month)}
      />
    </section>
  );
}

function StatCard({ label, value, accent, dot }) {
  return (
    <div className="border border-[var(--border)] rounded-xl p-3 sm:p-5 bg-[#13151D]">
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#A1A4B5] flex items-center gap-1.5 sm:gap-2">
        {dot && <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />} <span className="truncate">{label}</span>
      </div>
      <div
        className="font-serif-display text-2xl sm:text-3xl mt-1.5 sm:mt-2"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
